import { Button } from 'components/shared'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { postTaskAction, updateFiltersByQueryAction } from 'store/taskManager/actions'
import {
  AppContext,
  Box,
  Button as LibButton,
  FILTER_CONTROLLER_EVENTS,
  FiltersBadge,
  FiltersController,
  IPredicate,
  useHasUserTag,
  userEffectorModel,
  useSyncFilterValue,
  Text,
} from '@centra/centra-ui'

import { CreateTaskModalRemote } from 'components/CreateTaskModalRemote'
import { IPostClientTask } from 'store/taskManager/model'
import { IClientTaskFormData } from 'tasktracker/_types/models/task'
import { useStore, useUnit } from 'effector-react'
import { getTaskFilters } from './filters'
import { getInitialPredicates, mapTaskFilter } from './utils'
import queryString from 'query-string'
import { createBrowserHistory } from 'history'
import { FilterComponent } from './FilterComponent/FilterComponent'
import { getQueryObjFromPredicates } from './FilterComponent/utils'
import { AllFiltersModal } from './AllFiltersModal/AllFiltersModal'

import styled from './TaskFilter.module.scss'
import { PINNED_FILTERS } from './consts'

const TaskFilter = () => {
  const dispatch = useDispatch()
  const [isOpenCreateTask, setIsOpenCreateTask] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { $userStore: user } = useUnit(userEffectorModel)
  const userStore = useStore(userEffectorModel.$userStore)
  const { ngApiClient } = useContext(AppContext)
  const showCreateTaskModal = useCallback(() => setIsOpenCreateTask(true), [])
  const history = createBrowserHistory()
  const { search } = useLocation()
  const isAccountManager = useHasUserTag(['role.account-manager'])
  // Вообще по идеи это не нужно, но сейчас саппорты имеют тег role.account-manager,
  // поэтому нужна доп. фильтрация
  const isNotSupport = !useHasUserTag('role.support')

  const [query, setQuery] = useState(search.slice(1))
  const params = queryString.parse(query, { arrayFormat: 'bracket', parseNumbers: true })

  const initialPredicates = getInitialPredicates(params)
  const [openFilterModal, setOpenFilterModal] = useState(false)
  const [pinnedFilters, setPinnedFilters] = useState(localStorage.getItem(PINNED_FILTERS))

  const filterController = useRef(
    new FiltersController({
      scheme: mapTaskFilter(getTaskFilters({ httpClient: ngApiClient }), userStore?.tags),
      ngApiClient,
      predicates: initialPredicates as IPredicate[],
    })
  )
  const predicate = useSyncFilterValue({
    controller: filterController.current,
  })
  const value = filterController.current.getValueList()
  const filterScheme = useSyncFilterValue({
    controller: filterController.current,
    event: FILTER_CONTROLLER_EVENTS.SCHEME,
  })

  useEffect(() => {
    const params = queryString.parse(query, { arrayFormat: 'bracket', parseNumbers: true })
    history.replace(`${history.location.pathname}?${query}`)
    dispatch(updateFiltersByQueryAction(params))
  }, [query])

  const hasFilters = !!query

  const clearAllFilters = () => {
    setQuery('')
    filterController.current.onResetList(true)
  }
  const onSubmitTask = (data: IClientTaskFormData) => {
    setSubmitting(true)
    if (user?.id) {
      dispatch(
        postTaskAction(
          {
            ...(data as IPostClientTask),
            client: {
              name: '',
              id: user?.id,
            },
            location: 'advertiser_account',
          },
          () => {
            setSubmitting(false)
            setIsOpenCreateTask(false)
          }
        )
      )
    }
  }

  const filters = useMemo(() => {
    const set = new Set<string>()
    predicate.forEach(p => set.add(p.field))

    return set
  }, [value])

  useEffect(() => {
    const queryObj = getQueryObjFromPredicates(predicate)
    const q = queryString.stringify({ ...queryObj }, { arrayFormat: 'bracket' })
    setQuery(q)
  }, [predicate])

  return (
    <>
      <Box column gap={8}>
        <Box gap={8} justifyContent={'spaceBetween'}>
          <Box gap={8} wrap>
            {Array.from(filterScheme).map(f => (
              <FilterComponent
                key={f[0]}
                controller={filterController.current}
                query={query}
                filterName={f[0]}
                label={f[1].title}
                pinnedFilters={pinnedFilters}
              />
            ))}
            <Box style={{ position: 'relative' }}>
              {filterController.current?.count > 0 && (
                <Box className={styled.counter} alignItems={'center'} justifyContent={'center'}>
                  <Text id="filtersCounter" size="caption">
                    {filterController.current.count}
                  </Text>
                </Box>
              )}
              <LibButton
                id="allFiltersBtn"
                preIcon={'filter'}
                view="secondaryLight"
                onClick={() => setOpenFilterModal(true)}
              >
                Все фильтры
              </LibButton>
            </Box>
          </Box>

          {isAccountManager && isNotSupport && (
            <Box>
              <Button size={'sm'} onClick={showCreateTaskModal}>
                <svg width="16px" height="16px">
                  <use xlinkHref="#plus" />
                </svg>
                <span>Создать задачу</span>
              </Button>
            </Box>
          )}
        </Box>
        <Box gap={4} paddingBottom={20} wrap>
          {Array.from(filters).map(f => {
            const filterScheme = filterController.current.getScheme().get(f)
            if (!filterScheme) return null
            return <FiltersBadge key={f} controller={filterController.current} filterName={f} doNotGroup showYear />
          })}
          {hasFilters && (
            <LibButton id="removeAllFiltersBtn" size="sm" onClick={clearAllFilters} view="textLight">
              Очистить все
            </LibButton>
          )}
        </Box>
      </Box>
      <CreateTaskModalRemote
        isOpen={isOpenCreateTask}
        onClose={() => setIsOpenCreateTask(false)}
        onSubmit={onSubmitTask}
        submitting={submitting}
      />
      {openFilterModal && (
        <AllFiltersModal
          pinnedFilters={pinnedFilters}
          setPinnedFilters={setPinnedFilters}
          query={query}
          open={openFilterModal}
          controller={filterController.current}
          setOpenFilterModal={setOpenFilterModal}
          filterScheme={filterScheme}
        />
      )}
    </>
  )
}

export default TaskFilter
